import { read_is_company_head_user, read_is_department_head_user } from '../company/company_permissions'
import { find_current_team_user, read_identity_value } from '../users/user_identity'
import { read_leave_request_has_started } from './leave_days'

export const leave_approval_notifications_event_name = 'dolores_leave_approval_notifications_updated'

/* Let the navbar refresh approval notifications after a visible leave decision. */
export const emit_leave_approval_notifications_updated = () => {
    window.dispatchEvent(new Event(leave_approval_notifications_event_name))
}

/* Read the normalized status value used by approval checks and labels. */
export const read_leave_request_status = (status) => {
    return read_identity_value(status)
}

/* Read one stable key for notification cards and application rows. */
export const read_leave_request_key = (leaveRequest) => {
    return `${leaveRequest?.leaveRequestId || leaveRequest?._id || leaveRequest?.id || ''}`.trim()
}

/* Format stored ISO-like dates to the date style already used in this app. */
export const read_leave_request_date_label = (date) => {
    const [datePart] = `${date || ''}`.split('T')
    const [year, month, day] = datePart.split('-')
    const hasDateParts = Boolean(year && month && day)
    const labels = {
        true: `${day}-${month}-${year}`,
        false: ''
    }

    return labels[String(hasDateParts)]
}

/* Read the display name saved on a leave request. */
export const read_leave_request_owner_name = (leaveRequest) => {
    const fullName = `${leaveRequest?.firstName || ''} ${leaveRequest?.lastName || ''}`.trim()

    return fullName || leaveRequest?.email || leaveRequest?.username || ''
}

/* Map request statuses to the UI labels and semantic color tones. */
export const read_leave_request_status_config = (status, t) => {
    const statusMap = {
        approved: {
            label: t('Approved'),
            tone: 'success'
        },
        canceled: {
            label: t('Canceled'),
            tone: 'error'
        },
        rejected: {
            label: t('Rejected'),
            tone: 'error'
        },
        department_head_review: {
            label: t('Department head approval'),
            tone: 'warning'
        },
        company_head_review: {
            label: t('Company head approval'),
            tone: 'warning'
        }
    }

    return statusMap[read_leave_request_status(status)] || {
        label: t('Pending approval'),
        tone: 'warning'
    }
}

/* Read whether a request is currently inside a review stage. */
export const read_leave_request_review_status = (status) => {
    const reviewStatuses = ['department_head_review', 'company_head_review']

    return reviewStatuses.includes(read_leave_request_status(status))
}

/* Read whether the leave request is the personal leave type created by Apply. */
export const read_is_personal_leave_request = (leaveRequest) => {
    return read_identity_value(leaveRequest?.leaveType) === 'personal'
}

/* Build the current approver context once for all leave approval checks. */
export const read_leave_approval_context = (users, departments, currentUser) => {
    const currentTeamUser = find_current_team_user(users, currentUser)
    const currentUserDepartment = read_identity_value(currentTeamUser?.department)
    const currentUserIsDepartmentHead = read_is_department_head_user(currentTeamUser, departments)
    const currentUserIsCompanyHead = read_is_company_head_user(currentTeamUser, departments)
    const currentRoleValues = [
        ...(currentTeamUser?.roles || []),
        ...(currentTeamUser?.authRoles || []),
        ...(currentUser?.roles || []),
        ...(currentUser?.authRoles || []),
        ...(currentUser?.realm_access?.roles || []),
        ...Object.values(currentUser?.resource_access || {}).flatMap((resourceAccess) => {
            return resourceAccess?.roles || []
        })
    ].map((role) => read_identity_value(role))
    const currentUserIsLeaveAdmin = currentRoleValues.includes('king') || currentRoleValues.includes('admin')

    return {
        currentTeamUser,
        currentUserDepartment,
        currentUserIsDepartmentHead,
        currentUserIsCompanyHead,
        currentUserIsLeaveAdmin
    }
}

/* Read whether the request belongs to the same department as the approver. */
export const read_leave_request_same_department = (leaveRequest, approvalContext) => {
    const requestDepartment = read_identity_value(leaveRequest?.department)

    return Boolean(approvalContext.currentUserDepartment && approvalContext.currentUserDepartment === requestDepartment)
}

/* Read whether a department head can approve this exact request now. */
export const read_can_department_head_approve_leave_request = (leaveRequest, approvalContext) => {
    const sameDepartment = read_leave_request_same_department(leaveRequest, approvalContext)
    const departmentHeadReview = read_leave_request_status(leaveRequest?.status) === 'department_head_review'

    return approvalContext.currentUserIsDepartmentHead && sameDepartment && departmentHeadReview
}

/* Read whether a company head can approve this exact request now. */
export const read_can_company_head_approve_leave_request = (leaveRequest, approvalContext) => {
    const companyHeadReview = read_leave_request_status(leaveRequest?.status) === 'company_head_review'

    return approvalContext.currentUserIsCompanyHead && companyHeadReview
}

/* Read whether the current user can make an approve or decline decision. */
export const read_can_act_on_leave_request = (leaveRequest, users, departments, currentUser) => {
    const approvalContext = read_leave_approval_context(users, departments, currentUser)
    const departmentHeadAction = read_can_department_head_approve_leave_request(leaveRequest, approvalContext)
    const companyHeadAction = read_can_company_head_approve_leave_request(leaveRequest, approvalContext)
    const leaveAdminAction = approvalContext.currentUserIsLeaveAdmin && read_leave_request_review_status(leaveRequest?.status)

    return departmentHeadAction || companyHeadAction || leaveAdminAction
}

/* Read whether the current user can cancel a future approved leave request. */
export const read_can_cancel_leave_approval = (leaveRequest, users, departments, currentUser) => {
    const approvalContext = read_leave_approval_context(users, departments, currentUser)
    const leaveRequestStatus = read_leave_request_status(leaveRequest?.status)
    const futureLeaveRequest = !read_leave_request_has_started(leaveRequest)
    const sameDepartment = read_leave_request_same_department(leaveRequest, approvalContext)
    const departmentHeadCanCancel = approvalContext.currentUserIsDepartmentHead && sameDepartment
    const companyHeadCanCancel = approvalContext.currentUserIsCompanyHead
    const leaveAdminCanCancel = approvalContext.currentUserIsLeaveAdmin

    return leaveRequestStatus === 'approved'
        && futureLeaveRequest
        && (departmentHeadCanCancel || companyHeadCanCancel || leaveAdminCanCancel)
}

/* Keep future requests that belong to the current reviewer visibility rules. */
export const filter_application_leave_requests = (leaveRequests, users, departments, currentUser) => {
    const approvalContext = read_leave_approval_context(users, departments, currentUser)

    return (leaveRequests || []).filter((leaveRequest) => {
        const futureLeaveRequest = !read_leave_request_has_started(leaveRequest)
        const sameDepartment = read_leave_request_same_department(leaveRequest, approvalContext)
        const visibleForDepartmentHead = approvalContext.currentUserIsDepartmentHead && sameDepartment
        const visibleForCompanyHead = approvalContext.currentUserIsCompanyHead
        const visibleForLeaveAdmin = approvalContext.currentUserIsLeaveAdmin
        const visibleLeaveRequest = visibleForDepartmentHead || visibleForCompanyHead || visibleForLeaveAdmin
        const reviewStatus = read_leave_request_review_status(leaveRequest?.status)
        const approvedStatus = read_leave_request_status(leaveRequest?.status) === 'approved'

        return visibleLeaveRequest && (reviewStatus || (approvedStatus && futureLeaveRequest))
    })
}

/* Keep only personal leave requests the current department head can approve now. */
export const filter_personal_leave_approval_notifications = (leaveRequests, users, departments, currentUser) => {
    const approvalContext = read_leave_approval_context(users, departments, currentUser)

    return (leaveRequests || []).filter((leaveRequest) => {
        const personalLeaveRequest = read_is_personal_leave_request(leaveRequest)
        const futureLeaveRequest = !read_leave_request_has_started(leaveRequest)
        const canApproveRequest = read_can_department_head_approve_leave_request(leaveRequest, approvalContext)

        return personalLeaveRequest && futureLeaveRequest && canApproveRequest
    })
}
